import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Megaphone, MousePointerClick, Users, Target, ShoppingBag, Smartphone,
  TrendingUp, Lock, Unlock, ChevronDown, ChevronUp, AlertTriangle,
} from 'lucide-react';
import { UNTESTED_BID_STRATEGIES } from '@/lib/meta-matrix';
import { useFbWorkspace } from '@/contexts/FbWorkspaceContext';
import { facebookCampaignsService } from '@/services/facebook-campaigns.service';
import { facebookMetaOptionsService, MetaOptionsResponse } from '@/services/facebook-meta-options.service';
import { useMetaAccountCurrency } from '@/hooks/useMetaAccountCurrency';
import { formatBudgetHint } from '@/utils/adPlatformMinBudgets';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { DateTimePicker } from '@/components/DateTimePicker';

// ─── Objective icons ──────────────────────────────────────────────────────────

const OBJECTIVE_ICONS: Record<string, React.ReactNode> = {
  OUTCOME_AWARENESS:     <TrendingUp size={18} />,
  OUTCOME_TRAFFIC:       <MousePointerClick size={18} />,
  OUTCOME_ENGAGEMENT:    <Users size={18} />,
  OUTCOME_LEADS:         <Target size={18} />,
  OUTCOME_SALES:         <ShoppingBag size={18} />,
  OUTCOME_APP_PROMOTION: <Smartphone size={18} />,
};

// ─── Small segmented control ─────────────────────────────────────────────────

interface SegmentedProps<T extends string> {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}

function Segmented<T extends string>({ options, value, onChange }: SegmentedProps<T>) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-surface-alt p-0.5 gap-0.5">
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={clsx(
            'rounded-md px-3 py-1.5 text-xs font-medium transition-all',
            o.value === value
              ? 'bg-surface text-text-primary shadow-soft'
              : 'text-text-muted hover:text-text-primary',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── CampaignFormPanel ────────────────────────────────────────────────────────

export const CampaignFormPanel: React.FC = () => {
  const {
    campaign, updateCampaignForm, lockCampaign, unlockCampaign,
    setActiveLevel, setIsSubmitting, isSubmitting,
    adSets, addAdSet, appPromotion,
  } = useFbWorkspace();

  // Must create an ad set when none exists — setting activeLevel='adset' with
  // a null id renders an unsavable ghost form (sync no-ops, Save early-returns).
  const goToAdSets = () => {
    if (adSets.length > 0) setActiveLevel('adset', adSets[0].id, null);
    else addAdSet();
  };
  const [opts, setOpts] = useState<MetaOptionsResponse | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { currency: defaultCurrency } = useMetaAccountCurrency();
  const currency = appPromotion?.accountCurrency ?? defaultCurrency;
  const budgetHint = formatBudgetHint('meta', currency)
    ?? 'Daily and Lifetime are mutually exclusive — pick one.';

  // form state (mirror to context on change)
  const fd = campaign.formData as Record<string, string>;
  const name       = (fd.name       ?? '') as string;
  const objective  = (fd.objective  ?? '') as string;
  const buyingType = (fd.buying_type ?? 'AUCTION') as 'AUCTION' | 'RESERVED';
  const bidStrat   = (fd.bid_strategy ?? '') as string;
  const budgetAt   = (fd.budget_at ?? 'campaign') as 'campaign' | 'adset';
  const budgetKind = (fd.budget_kind ?? 'daily') as 'daily' | 'lifetime';
  const budget     = (fd.budget ?? fd.daily_budget ?? fd.lifetime_budget ?? '') as string;
  const spendCap   = (fd.spend_cap ?? '') as string;
  const startTime       = (fd.start_time ?? '') as string;
  const stopTime        = (fd.stop_time ?? '') as string;
  const specialCats     = (fd.special_ad_categories ? JSON.parse(fd.special_ad_categories) : []) as string[];

  useEffect(() => {
    facebookMetaOptionsService.get().then(setOpts).catch(() => undefined);
  }, []);

  const set = (key: string, val: string) => updateCampaignForm({ [key]: val });

  // "None" means no category, so it can't sit next to a real one.
  const toggleSpecialCat = (code: string) => {
    let next = specialCats.includes(code)
      ? specialCats.filter(c => c !== code)
      : [...specialCats, code];
    if (code === 'NONE' && next.includes('NONE')) next = ['NONE'];
    else if (code !== 'NONE') next = next.filter(c => c !== 'NONE');
    set('special_ad_categories', JSON.stringify(next));
  };

  const handleSave = async () => {
    if (!name || !objective) { toast.error('Name and objective are required'); return; }

    // Friendly Meta-validator surrogates so non-developers get readable errors
    // before we hit the API.
    if (budgetAt === 'campaign' && budgetKind === 'lifetime' && budget && !stopTime) {
      toast.error('Lifetime budget needs a stop time. Pick when the campaign should stop.');
      return;
    }
    if (startTime && stopTime && new Date(stopTime).getTime() <= new Date(startTime).getTime()) {
      toast.error('Stop time must be after the start time.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        name,
        objective,
        buying_type: buyingType,
        status: 'PAUSED',
        expected_account_uid: appPromotion?.accountUid,
        special_ad_categories: specialCats,
      };
      if (bidStrat) payload.bid_strategy = bidStrat;
      if (budgetAt === 'campaign' && budget) {
        const minor = String(Math.round(parseFloat(budget) * 100));
        if (budgetKind === 'daily') payload.daily_budget = minor;
        else payload.lifetime_budget = minor;
      }
      if (spendCap) payload.spend_cap = String(Math.round(parseFloat(spendCap) * 100));
      if (startTime) payload.start_time = startTime;
      if (stopTime) payload.stop_time = stopTime;

      const res = await facebookCampaignsService.create(payload as unknown as Parameters<typeof facebookCampaignsService.create>[0]);
      lockCampaign(res.id, res.id);
      toast.success('Campaign saved');
      goToAdSets();
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 w-full">
      {/* Campaign name */}
      <Card padded>
        <div className="flex items-center gap-2 mb-4">
          <Megaphone size={16} className="text-primary" />
          <h3 className="font-semibold text-text-primary">Campaign</h3>
          {campaign.isLocked && (
            <span className="ml-auto flex items-center gap-1 text-xs text-warning">
              <Lock size={12} /> Locked
              <button type="button" onClick={unlockCampaign} className="ml-1 underline">Edit</button>
            </span>
          )}
        </div>

        <FormField label="Campaign name" required>
          <Input
            value={name}
            onChange={e => set('name', e.target.value)}
            placeholder="e.g. Spring Sale — UAE"
            disabled={campaign.isLocked}
          />
        </FormField>
      </Card>

      {/* Objective cards */}
      <Card padded>
        <h4 className="text-sm font-semibold text-text-primary mb-3">Objective</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {(opts?.objectives ?? FALLBACK_OBJECTIVES).map(obj => {
            const selected = objective === obj.code;
            return (
              <button
                key={obj.code}
                type="button"
                disabled={campaign.isLocked || !!appPromotion}
                onClick={() => set('objective', obj.code)}
                className={clsx(
                  'flex flex-col gap-2 rounded-xl border p-3 text-left transition-all',
                  selected
                    ? 'border-primary bg-primary-soft ring-1 ring-primary'
                    : 'border-border bg-surface hover:border-border-strong hover:bg-surface-alt',
                  'disabled:opacity-60 disabled:cursor-not-allowed',
                )}
              >
                <span className={clsx('text-sm', selected ? 'text-primary' : 'text-text-muted')}>
                  {OBJECTIVE_ICONS[obj.code] ?? <Megaphone size={18} />}
                </span>
                <div>
                  <div className="text-xs font-semibold text-text-primary">{obj.label}</div>
                  {'description' in obj && (
                    <div className="text-2xs text-text-muted mt-0.5 line-clamp-2">{(obj as { description: string }).description}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Buying type + bid strategy */}
      <Card padded>
        <h4 className="text-sm font-semibold text-text-primary mb-3">Buying type</h4>
        <Segmented
          options={[{ value: 'AUCTION', label: 'Auction' }, { value: 'RESERVED', label: 'Reservation' }]}
          value={buyingType}
          onChange={v => set('buying_type', v)}
        />
        {buyingType === 'RESERVED' && (
          <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-warning/40 bg-warning-soft px-2.5 py-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0 text-warning" />
            <span className="text-2xs leading-snug text-warning">
              Not supported yet — Meta runs Reservation buying through a separate reach &amp; frequency
              flow that this builder doesn&apos;t implement (verified: standard create is rejected).
              Use Auction; it covers all six objectives.
            </span>
          </div>
        )}

        {buyingType === 'AUCTION' && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-text-primary mb-3">Bid strategy</h4>
            <div className="grid grid-cols-2 gap-2">
              {(opts?.bidStrategies ?? FALLBACK_BID_STRATEGIES).map(bs => (
                <button
                  key={bs.code}
                  type="button"
                  disabled={campaign.isLocked}
                  onClick={() => set('bid_strategy', bs.code)}
                  className={clsx(
                    'rounded-xl border px-3 py-2.5 text-left transition-all',
                    bidStrat === bs.code
                      ? 'border-primary bg-primary-soft'
                      : 'border-border bg-surface hover:border-border-strong',
                    'disabled:opacity-60',
                  )}
                >
                  <div className="text-xs font-semibold text-text-primary">{bs.label}</div>
                  {'description' in bs && bs.description && (
                    <div className="text-2xs text-text-muted mt-0.5">{bs.description}</div>
                  )}
                  {(UNTESTED_BID_STRATEGIES as readonly string[]).includes(bs.code) && (
                    <div className="mt-1 flex items-center gap-1 text-2xs font-medium text-warning">
                      <AlertTriangle size={10} /> Not live-tested yet
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Budget at */}
      <Card padded>
        <h4 className="text-sm font-semibold text-text-primary mb-1">Budget control</h4>
        <p className="text-xs text-text-muted mb-3">CBO lets Meta optimise across ad sets; ABO gives each ad set its own budget.</p>
        <Segmented
          options={[
            { value: 'campaign', label: 'Campaign (CBO)' },
            { value: 'adset', label: 'Ad Set (ABO)' },
          ]}
          value={budgetAt}
          onChange={v => set('budget_at', v)}
        />
        {/* CBO verified live 2026-08-28 (daily, lifetime, and with bid cap —
            API sweep + full UI chain), so no warning here anymore. */}

        {budgetAt === 'campaign' && (
          <div className="mt-4 space-y-3">
            <FormField label="Budget" hint={budgetHint}>
              <div className="flex items-center gap-2">
                <Segmented
                  options={[
                    { value: 'daily',    label: 'Daily' },
                    { value: 'lifetime', label: 'Lifetime' },
                  ]}
                  value={budgetKind}
                  onChange={v => set('budget_kind', v)}
                />
                <Input
                  type="number"
                  min="1"
                  value={budget}
                  onChange={e => set('budget', e.target.value)}
                  placeholder={budgetKind === 'daily' ? '50' : '1000'}
                  disabled={campaign.isLocked}
                />
                <span className="text-xs font-medium text-text-muted">{currency ?? '—'}</span>
              </div>
            </FormField>

            <FormField label="Campaign spending limit (optional)" hint="Hard cap on total spend across the lifetime of this campaign.">
              <Input
                type="number"
                min="1"
                value={spendCap}
                onChange={e => set('spend_cap', e.target.value)}
                placeholder="e.g. 5000"
                disabled={campaign.isLocked}
              />
            </FormField>
          </div>
        )}
        {budgetAt === 'adset' && (
          <div className="mt-3 space-y-3">
            <p className="text-xs text-info bg-info-soft rounded-lg px-3 py-2">
              Budget will be set per ad set (ABO).
            </p>
            <FormField label="Campaign spending limit (optional)" hint="Hard cap on total spend across all ad sets in this campaign.">
              <Input
                type="number"
                min="1"
                value={spendCap}
                onChange={e => set('spend_cap', e.target.value)}
                placeholder="e.g. 5000"
                disabled={campaign.isLocked}
              />
            </FormField>
          </div>
        )}
      </Card>

      {/* Schedule + Special categories (collapsed by default) */}
      <Card padded>
        <button
          type="button"
          onClick={() => setShowAdvanced(v => !v)}
          className="flex w-full items-center justify-between text-sm font-semibold text-text-primary"
        >
          Advanced options
          {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {showAdvanced && (
          <div className="mt-4 space-y-4">
            {/* Schedule */}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Start time">
                <DateTimePicker value={startTime} onChange={v => set('start_time', v)} disabled={campaign.isLocked} placeholder="Start date & time" />
              </FormField>
              <FormField label="Stop time">
                <DateTimePicker value={stopTime} onChange={v => set('stop_time', v)} disabled={campaign.isLocked} placeholder="Stop date & time" />
              </FormField>
            </div>

            {/* Special ad categories */}
            {(opts?.specialAdCategories ?? []).length > 0 && (
              <div>
                <p className="text-xs font-medium text-text-secondary mb-2">Special ad categories (if applicable)</p>
                <div className="flex flex-wrap gap-1.5">
                  {opts!.specialAdCategories.map(cat => (
                    <button
                      key={cat.code}
                      type="button"
                      onClick={() => toggleSpecialCat(cat.code)}
                      className={clsx(
                        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                        specialCats.includes(cat.code)
                          ? 'border-warning bg-warning-soft text-warning'
                          : 'border-border text-text-muted hover:border-border-strong',
                      )}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Save button */}
      {!campaign.isLocked && (
        <Button
          variant="primary"
          loading={isSubmitting}
          onClick={handleSave}
          disabled={!name || !objective}
        >
          Save Campaign &amp; Continue
        </Button>
      )}

      {campaign.isLocked && (
        <div className="flex items-center gap-2 text-xs text-success">
          <Lock size={13} />
          Campaign saved — proceed to ad sets
          <button type="button" onClick={goToAdSets} className="ml-2 underline text-primary">
            Go to ad sets
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Fallback data (shown while meta-options loads) ───────────────────────────

const FALLBACK_OBJECTIVES = [
  { code: 'OUTCOME_AWARENESS',     label: 'Awareness',       description: 'Reach people who are likely to remember your ad' },
  { code: 'OUTCOME_TRAFFIC',       label: 'Traffic',         description: 'Send people to a destination' },
  { code: 'OUTCOME_ENGAGEMENT',    label: 'Engagement',      description: 'Get more post interactions' },
  { code: 'OUTCOME_LEADS',         label: 'Leads',           description: 'Collect leads for your business' },
  { code: 'OUTCOME_SALES',         label: 'Sales',           description: 'Find people likely to purchase' },
  { code: 'OUTCOME_APP_PROMOTION', label: 'App Promotions',  description: 'Find new users for your app' },
];

const FALLBACK_BID_STRATEGIES = [
  { code: 'LOWEST_COST_WITHOUT_CAP', label: 'Lowest cost',     description: 'Get the most results for your budget' },
  { code: 'LOWEST_COST_WITH_BID_CAP', label: 'Bid cap',        description: 'Set a maximum bid amount' },
  { code: 'COST_CAP',                label: 'Cost cap',         description: 'Maintain a target cost per result' },
  // TARGET_COST was deprecated by Meta for outcome-based (ODAX) objectives —
  // the API rejects it as "Invalid parameter" for all current objectives.
  // Removed to prevent users from picking an unusable strategy.
];
